import { z as zod } from 'zod';

import { BaseDatastreamDtoSchema } from '@enterprise/types';

/**
 * Zod schema for validating Mailing List objects.
 *
 * Extends the `BaseDatastreamDtoSchema` with additional mailing list-specific fields.
 *
 * Fields:
 * - `dataDueDate`: (string | undefined) The due date for data, if applicable.
 * - `displayName`: (string | undefined) The display name of the mailing list.
 * - `firstMailDate`: (string | undefined) The date of the first mail, if applicable.
 * - `groupId`: (number | undefined) The group ID associated with the mailing list.
 * - `groupNumber`: (number | undefined) The group number associated with the mailing list.
 * - `listsDueDate`: (string | undefined) The due date for the list, if applicable.
 * - `mailCode`: (string | undefined) The mail code for the mailing list.
 * - `mailingDisplayName`: (string | undefined) The display name for the mailing.
 * - `managerExternalId`: (number | undefined) The external ID of the manager.
 * - `managerId`: (number | undefined) The internal ID of the manager.
 * - `managerName`: (string | undefined) The name of the manager.
 * - `managerNiceName`: (string | undefined) The display-friendly name of the manager.
 * - `netQuantity`: (number | undefined) The net quantity for the mailing list.
 * - `orderedQuantity`: (number | undefined) The ordered quantity for the mailing list.
 * - `ownerExternalId`: (number | undefined) The external ID of the owner.
 * - `ownerId`: (number | undefined) The internal ID of the owner.
 * - `ownerName`: (string | undefined) The name of the owner.
 * - `ownerNiceName`: (string | undefined) The display-friendly name of the owner.
 * - `parentMailingDisplayName`: (string | undefined) The display name of the parent mailing list.
 * - `parentMailingListId`: (number | undefined) The ID of the parent mailing list.
 * - `parentMailingListName`: (string | undefined) The name of the parent mailing list.
 * - `previousId`: (number | undefined) The ID of the previous mailing list, if any.
 * - `priority`: (number | undefined) The priority of the mailing list.
 * - `purchaseOrderNumber`: (string | undefined) The purchase order number associated with the mailing list.
 * - `receivedQuantity`: (number | undefined) The received quantity for the mailing list.
 * - `scoringServiceId`: (number | undefined) The ID of the scoring service.
 * - `scoringServiceModelId`: (number | undefined) The ID of the scoring service model.
 * - `scoringServiceModelName`: (string | undefined) The name of the scoring service model.
 * - `scoringServiceName`: (string | undefined) The name of the scoring service.
 * - `statusId`: (number | undefined) The status ID of the mailing list.
 * - `targetQuantity`: (number | undefined) The target quantity for the mailing list.
 * - `workflowMailingListId`: (number | undefined) The workflow mailing list ID.
 */
export const MailingListSchema = BaseDatastreamDtoSchema.extend({
  dataDueDate: zod.string().optional(),
  displayName: zod.string().optional(),
  firstMailDate: zod.string().optional(),
  groupId: zod.number().optional(),
  groupNumber: zod.number().optional(),
  listsDueDate: zod.string().optional(),
  mailCode: zod.string().optional(),
  mailingDisplayName: zod.string().optional(),
  managerExternalId: zod.number().optional(),
  managerId: zod.number().optional(),
  managerName: zod.string().optional(),
  managerNiceName: zod.string().optional(),
  netQuantity: zod.number().optional(),
  orderedQuantity: zod.number().optional(),
  ownerExternalId: zod.number().optional(),
  ownerId: zod.number().optional(),
  ownerName: zod.string().optional(),
  ownerNiceName: zod.string().optional(),
  parentMailingDisplayName: zod.string().optional(),
  parentMailingListId: zod.number().optional(),
  parentMailingListName: zod.string().optional(),
  previousId: zod.number().optional(),
  priority: zod.number().optional(),
  purchaseOrderNumber: zod.string().optional(),
  receivedQuantity: zod.number().optional(),
  scoringServiceId: zod.number().optional(),
  scoringServiceModelId: zod.number().optional(),
  scoringServiceModelName: zod.string().optional(),
  scoringServiceName: zod.string().optional(),
  statusId: zod.number().optional(),
  targetQuantity: zod.number().optional(),
  workflowMailingListId: zod.number().optional(),
});
